import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
} from 'lexical'
import { sanitize } from 'dompurify'
import { filterRegex } from 'selectors/search/searchFilters'
import getSearchQueryPartAtPosition from 'util/search/getSearchQueryPartAtPosition'
import storage from 'util/storage'
import { escapeRegExp } from 'util/strings'

const RECENT_TICKETS_SEARCH_QUERIES_STORAGE_KEY = 'recentTicketsSearchQueries'
export const CLEAR_SEARCH_TAG = 'clear-search'

export const getFilterMatchForQuery = searchQuery => {
  const regex = new RegExp(filterRegex)

  return regex.exec(searchQuery)
}

export const removeWhitspacesAfterColon = (queryString = '') =>
  queryString.trim().replace(/:\s+/g, ':')

export const mergeRegister = (...func) => {
  return () => {
    func.forEach(f => f())
  }
}

export const recentSearchQueries = {
  get value() {
    return JSON.parse(
      storage.get(RECENT_TICKETS_SEARCH_QUERIES_STORAGE_KEY) || '[]'
    )
  },
  set value(query) {
    if (!query) return
    const queries = this.value
    const index = queries.indexOf(query)
    if (index > -1) {
      queries.splice(index, 1)
    }
    // Put the latest query at the beginning of the list
    queries.unshift(query)
    if (queries.length > 100) {
      queries.pop()
    }
    storage.set(
      RECENT_TICKETS_SEARCH_QUERIES_STORAGE_KEY,
      JSON.stringify(queries)
    )
  },
}

export const getMatchedRecentSearchQueries = (searchQuery = '') => {
  const maxLength = 3
  const storedSearches = recentSearchQueries.value
  if (searchQuery) {
    const regex = new RegExp(escapeRegExp(searchQuery), 'i')
    return storedSearches
      .filter(query => regex.test(query))
      .splice(0, maxLength)
  }
  return storedSearches.splice(0, maxLength)
}

// eslint-disable-next-line no-underscore-dangle
export const getTextFromRoot = root => root.__cachedText

export const getCurrentPart = (node, root) => {
  if (root.isEmpty()) {
    return undefined
  }
  // eslint-disable-next-line no-underscore-dangle
  if (node.__type !== 'filter') {
    const selection = $getSelection()
    return getSearchQueryPartAtPosition(
      node.getTextContent(),
      selection.anchor.offset
    )
  }
  return getSearchQueryPartAtPosition(
    removeWhitspacesAfterColon(node.getTextContent()),
    0
  )
}

export const decorateFilterInMenu = text => {
  if (!text || !text.trim()) return ''

  const relaced = text.replace(
    new RegExp(`${filterRegex}`, 'g'),
    `<span class="search-filter">$1</span>`
  )

  return sanitize(relaced, { ALLOWED_TAGS: ['span'] })
}

export const clearEditorAndAddBaseNode = () => {
  const root = $getRoot()
  const p = $createParagraphNode()
  root.clear().append(p)
  p.select()
}

// Reset the editor state when the search query is submitted to only show keywords
// This is to prevent filters from being persisted in the editor, since we show them in filter badges
export const updateEditorContentAfterSubmit = (editor, keywords) => {
  editor.update(
    () => {
      const root = $getRoot()
      if (keywords.length === 0) {
        clearEditorAndAddBaseNode()
        return
      }
      const p = $createParagraphNode()
      p.append($createTextNode(keywords.join(' ')))
      root.clear()
      root.append(p)
    },
    {
      onUpdate: () => {
        // Editor update will add selection, blur the editor like normal submit
        editor.blur()
      },
      skipTransforms: true,
    }
  )
}

// Replace the current selection with the new filter node, the split symbol is space
export const textNodeTransformAfterSelectFilter = (
  node,
  selection,
  newFilterNode
) => {
  let targetNode
  const text = node.getTextContent()
  const currentOffset = selection.anchor.offset
  const whitespaceArray = []

  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === ' ') {
      whitespaceArray.push(i)
    }
  }

  const closestToOffset =
    whitespaceArray.find(index => index >= currentOffset) ||
    whitespaceArray[whitespaceArray.length - 1] ||
    0

  const splitTextIndexArray = [
    whitespaceArray[whitespaceArray.indexOf(closestToOffset) - 1],
    closestToOffset,
  ].filter(index => index !== undefined)

  const splitNodes = node.splitText(...splitTextIndexArray)
  targetNode =
    splitNodes[
      currentOffset < splitTextIndexArray[splitTextIndexArray.length - 1]
        ? splitTextIndexArray.length - 1
        : splitNodes.length - 1
    ]
  targetNode = targetNode || splitNodes[0]
  targetNode.replace(newFilterNode)
}
